/**
 * Bridge between Supabase data and @cascade/core's Project interface.
 *
 * Constructs a fully-compatible Project object from SaaS data so
 * the full Cascade <Editor /> can render inside the SaaS app.
 */

import {
  Logger,
  ProjectMetadata,
  createSettingsMetadata,
  type AudioClipConfig,
  type AudioTrackConfig,
  type Project,
  type RemotionSceneDescription,
} from '@cascade/core';
import {RemotionPlayerAdapter} from '../remotion/RemotionPlayerAdapter';
import {RemotionRendererStub} from '../remotion/RemotionRendererStub';
import {PresenterStub} from '../remotion/PresenterStub';
import type {Scene} from './db';
import {compileToComponent} from './compiler';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CompiledScene {
  id: string;
  name: string;
  component: React.FC | null;
  error: string | null;
  code: string;
  durationFrames: number;
}

export interface SaaSProjectBundle {
  project: Project;
  player: RemotionPlayerAdapter;
  renderer: RemotionRendererStub;
  presenter: PresenterStub;
  meta: ProjectMetadata;
  settings: ReturnType<typeof createSettingsMetadata>;
}

// ---------------------------------------------------------------------------
// Scene compilation
// ---------------------------------------------------------------------------

/**
 * Compile an array of Supabase Scene records into CompiledScene objects.
 * Runs compilations in parallel for speed.
 */
export async function compileScenes(scenes: Scene[]): Promise<CompiledScene[]> {
  const results = await Promise.all(
    scenes.map(async (scene): Promise<CompiledScene> => {
      if (!scene.code.trim()) {
        return {
          id: scene.id,
          name: scene.name,
          component: null,
          error: 'Empty scene code',
          code: scene.code,
          durationFrames: scene.duration_frames,
        };
      }

      const {component, error} = await compileToComponent(scene.code);
      return {
        id: scene.id,
        name: scene.name,
        component,
        error,
        code: scene.code,
        durationFrames: scene.duration_frames,
      };
    }),
  );

  return results;
}

/**
 * Convert compiled scenes into RemotionSceneDescription[] for the player.
 */
export function toRemotionScenes(
  compiled: CompiledScene[],
  fps: number,
): RemotionSceneDescription[] {
  return compiled
    .filter(s => s.component !== null)
    .map(s => ({
      name: s.name,
      fps,
      durationInFrames: s.durationFrames || fps * 10,
      component: () => Promise.resolve({default: s.component!}),
    }));
}

// ---------------------------------------------------------------------------
// Project factory
// ---------------------------------------------------------------------------

/**
 * Create a full Project + player + renderer + presenter bundle from SaaS data.
 *
 * This mirrors the setup in `main.tsx:editor()` but uses in-browser
 * compiled scenes instead of Vite-transformed scene modules.
 */
export function createSaaSProject(opts: {
  name: string;
  compiledScenes: CompiledScene[];
  fps?: number;
  width?: number;
  height?: number;
  initialAudioClips?: AudioClipConfig[];
  initialAudioTracks?: AudioTrackConfig[];
  onPublishAudioClips?: (clips: AudioClipConfig[], tracks: AudioTrackConfig[]) => void;
}): SaaSProjectBundle {
  const fps = opts.fps ?? 30;
  const remotionScenes = toRemotionScenes(opts.compiledScenes, fps);

  const logger = new Logger();

  // Forward logs to console
  logger.onLogged.subscribe(log => {
    const {level, message, stack, object, durationMs, ...rest} = log;
    const fn = console[level as 'error'] ?? console.log;
    fn(message, ...[object, durationMs, rest].filter(part => !!part));
    if (stack) fn(stack);
  });

  // Build the Project object -- scenes is empty because we use remotionScenes
  const project: Project = {
    name: opts.name,
    scenes: [],
    remotionScenes,
    plugins: [],
    logger,
    // meta and settings are set below after we create them with the project ref
    meta: null as any,
    settings: null as any,
    versions: {core: '0.0.0', two: null, ui: null, vitePlugin: null},
    experimentalFeatures: false,
  };

  // ProjectMetadata needs a Project reference for the exporter field
  const meta = new ProjectMetadata(project);
  const settings = createSettingsMetadata();
  project.meta = meta;
  project.settings = settings;

  // Create the player adapter
  const player = new RemotionPlayerAdapter({
    fps,
    remotionScenes,
    logger,
    initialAudioClips: opts.initialAudioClips,
    initialAudioTracks: opts.initialAudioTracks,
    onPublishAudioClips: opts.onPublishAudioClips,
  });

  // Renderer stub (no HMR connection in SaaS mode -- rendering is client-side)
  const renderer = new RemotionRendererStub(null);

  // Presenter stub (no presentation mode)
  const presenter = new PresenterStub();

  return {project, player, renderer, presenter, meta, settings};
}

/**
 * Update an existing player adapter with new scenes.
 * Called after AI generates a new scene.
 */
export function updatePlayerScenes(
  player: RemotionPlayerAdapter,
  compiled: CompiledScene[],
  fps: number,
): void {
  const remotionScenes = toRemotionScenes(compiled, fps);
  // The playback manager can be re-setup with new descriptions
  (player.playback as any).setupFromDescriptions(remotionScenes);
}
